import { apiService } from "./api";

export interface ProductImage {
  id: string;
  imageUrl: string; // URL to image in MinIO
  displayOrder: number;
  altText: string | null;
}

export interface ProductVariant {
  id: string;
  variantType: string;
  variantValue: string;
  priceAdjustment: number;
  isAvailable: boolean;
  displayOrder: number;
}

export interface ProductSpecification {
  id: string;
  specKey: string;
  specValue: string;
  displayOrder: number;
}

export interface Category {
  id: string;
  name: string;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  basePrice: number;
  originalPrice: number | null;
  discountPercentage: number | null;
  rating: number | null;
  ratingCount: number | null;
  isActive?: boolean;
  inStock?: boolean;
  category: Category;
  images: ProductImage[];
  variants: ProductVariant[];
  specifications: ProductSpecification[];
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ProductFilters {
  categoryId?: string | string[];
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  limit?: number;
}

// Helper function to normalize product data (convert string decimals to numbers)
const normalizeProduct = (product: any): Product => {
  return {
    ...product,
    basePrice: Number(product.basePrice),
    originalPrice: product.originalPrice ? Number(product.originalPrice) : null,
    discountPercentage: product.discountPercentage
      ? Number(product.discountPercentage)
      : null,
    rating: product.rating ? Number(product.rating) : null,
    ratingCount: product.ratingCount ? Number(product.ratingCount) : null,
    variants:
      product.variants?.map((v: any) => ({
        ...v,
        priceAdjustment: Number(v.priceAdjustment),
      })) || [],
  };
};

export const productService = {
  getAll: async (filters?: ProductFilters): Promise<ProductsResponse> => {
    const params = new URLSearchParams();
    if (filters?.categoryId) {
      if (Array.isArray(filters.categoryId)) {
        filters.categoryId.forEach((id) => params.append("categoryId", id));
      } else {
        params.append("categoryId", filters.categoryId);
      }
    }
    if (filters?.search) params.append("search", filters.search);
    if (filters?.minPrice !== undefined && filters.minPrice !== null) {
      params.append("minPrice", filters.minPrice.toString());
    }
    if (filters?.maxPrice !== undefined && filters.maxPrice !== null) {
      params.append("maxPrice", filters.maxPrice.toString());
    }
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/products?${queryString}` : "/products";
    const response = await apiService.get<ProductsResponse>(endpoint);

    // Normalize products to convert string decimals to numbers
    return {
      ...response,
      products: response.products.map(normalizeProduct),
    };
  },

  getById: async (productId: string): Promise<Product> => {
    const product = await apiService.get<Product>(`/products/${productId}`);
    return normalizeProduct(product);
  },
};
